import { useState } from 'react';

import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowRight, ArrowUpDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Organisation } from '../../../api/types/backend-dto-types';
import { Input } from '@/components/atoms/input';
import { Button } from '@/components/atoms/new-button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/molecules/table';

interface Props {
  organisations: Organisation[];
  isLoading: boolean;
}

const OrganisationsTable = ({ organisations, isLoading }: Props) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [globalFilter, setGlobalFilter] = useState<string>('');

  const navigate = useNavigate();

  const handleRowClick = (orgId: string) => {
    navigate(`/app/chronos/organisation/${orgId}`);
  };

  const handleManageUsersClick = (orgId: string) => {
    navigate(`/app/chronos/organisation/${orgId}/users`);
  };

  const handleManageRunsClick = (orgId: string) => {
    navigate(`/app/chronos/organisation/${orgId}/runs`);
  };

  const columns: ColumnDef<Organisation>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => {
        return (
          <div className="text-left">
            <div
              className="flex items-center gap-1 hover:cursor-pointer"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            >
              Organisation Name
              <ArrowUpDown className="h-4 w-4 text-gray-700" />
            </div>
          </div>
        );
      },
      cell: ({ row }) => {
        return <div className="text-left">{row.getValue('name')}</div>;
      },
    },
    {
      accessorKey: 'userCount',
      header: ({ column }) => {
        return (
          <div className="text-left">
            <div
              className="flex items-center gap-1 hover:cursor-pointer"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            >
              Users
              <ArrowUpDown className="h-4 w-4 text-gray-700" />
            </div>
          </div>
        );
      },
      cell: ({ row }) => {
        const usersCount = row.getValue('userCount') as number;
        return (
          <div className="flex items-center gap-5">
            <span className="inline-flex rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
              {usersCount}
            </span>

            {usersCount > 0 && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleManageUsersClick(row.original.id);
                }}
                className="flex h-auto items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 shadow-sm transition-all hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600"
                aria-label="View user matters"
              >
                <span className="text-xs font-medium">Manage</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'matterCount',
      header: ({ column }) => {
        return (
          <div className="text-left">
            <div
              className="flex items-center gap-1 hover:cursor-pointer"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            >
              Matters
              <ArrowUpDown className="h-4 w-4 text-gray-700" />
            </div>
          </div>
        );
      },
      cell: ({ row }) => {
        const matters = row.getValue('matterCount') as number;
        return <span className="inline-flex rounded-full px-2 py-1 text-xs font-medium">{matters}</span>;
      },
    },
    {
      accessorKey: 'activeRuns',
      header: ({ column }) => {
        return (
          <div className="text-left">
            <div
              className="flex items-center gap-1 hover:cursor-pointer"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            >
              Runs
            </div>
          </div>
        );
      },
      cell: ({ row }) => {
        return (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleManageRunsClick(row.original.id);
            }}
            className="flex h-auto items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 shadow-sm transition-all hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600"
          >
            <span className="text-xs font-medium">Manage Runs</span>
          </Button>
        );
      },
    },
  ];

  // Create table instance
  const table = useReactTable({
    data: organisations || [],
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      globalFilter,
    },
  });

  return (
    <div className="flex w-full flex-col justify-center gap-4 rounded-xl bg-white p-6 shadow-sm">
      <div className="flex w-full flex-row items-center justify-between gap-2">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold text-gray-800">All Organisations</h2>
        </div>

        <Input
          placeholder="Search by organisation name..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="max-w-sm"
        />
      </div>

      <div className="w-full overflow-x-auto rounded-xl border border-gray-200 text-sm">
        {isLoading ? (
          <div className="flex h-60 items-center justify-center p-8">
            <FontAwesomeIcon icon={faCircleNotch} className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <Table className="w-full table-fixed">
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => (
                      <TableHead key={header.id} className={index === 0 ? 'w-full' : 'w-[250px]'}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row, index) => {
                    return (
                      <TableRow
                        key={row.id}
                        className={`h-16 hover:cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                        onClick={() => handleRowClick(row.original.id)}
                      >
                        {row.getVisibleCells().map((cell, cellIndex) => (
                          <TableCell key={cell.id} className={cellIndex === 0 ? 'w-full' : 'w-[250px]'}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center text-gray-500">
                      No organisations found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center justify-end space-x-2 py-4">
        <div className="flex-1 text-sm text-gray-500">
          Showing {table.getFilteredRowModel().rows.length} organisations
        </div>
        <div className="space-x-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button variant="secondary" size="sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrganisationsTable;
